import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { FaEye, FaCheck, FaTimes } from 'react-icons/fa';

const TAB_ALL = 'all';
const TAB_CONSTRUCTION = 'construction';
const TAB_HEALTH = 'health';

const BusinessManagement: React.FC = () => {
  const { t } = useTranslation();
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{message: string, type: 'success'|'error'}|null>(null);
  const [activeTab, setActiveTab] = useState<string>(TAB_ALL);

  useEffect(() => {
    const fetchBusinesses = async () => {
      setLoading(true);
      const db = getFirestore();
      const businessesSnap = await getDocs(collection(db, 'businesses'));
      const pendingBusinesses: any[] = [];
      businessesSnap.forEach(docu => {
        const data = docu.data();
        if (data.status === 'pending') {
          pendingBusinesses.push({ id: docu.id, ...data });
        }
      });
      setBusinesses(pendingBusinesses);
      setLoading(false);
    };
    fetchBusinesses();
  }, []);

  const handleView = (business: any) => {
    setSelected(business);
    setModalOpen(true);
  };
  const handleClose = () => {
    setModalOpen(false);
    setSelected(null);
  };
  const handleApprove = async () => {
    if (!selected) return;
    const db = getFirestore();
    await updateDoc(doc(db, 'businesses', selected.id), { status: 'active' });
    setBusinesses(w => w.filter(u => u.id !== selected.id));
    setToast({ message: t('common.updateSuccess'), type: 'success' });
    setTimeout(() => setToast(null), 2500);
    handleClose();
  };
  const handleReject = async () => {
    if (!selected) return;
    const db = getFirestore();
    await updateDoc(doc(db, 'businesses', selected.id), { status: 'rejected' });
    setBusinesses(w => w.filter(u => u.id !== selected.id));
    setToast({ message: t('common.updateSuccess'), type: 'success' });
    setTimeout(() => setToast(null), 2500);
    handleClose();
  };

  // Filtrado por tabs
  const filteredBusinesses = businesses.filter(business => {
    if (activeTab === TAB_ALL) return true;
    if (activeTab === TAB_HEALTH) return business.companySector === 'health';
    if (activeTab === TAB_CONSTRUCTION) return !business.companySector || business.companySector !== 'health';
    return true;
  });

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-red-50 via-white to-red-100 py-12 px-4 flex flex-col items-center justify-start">
      <div className="max-w-7xl w-full mx-auto">
        <h2 className="text-4xl font-extrabold text-gray-900 mb-10 tracking-tight drop-shadow-lg">
          <span className="bg-gradient-to-r from-red-500 via-pink-400 to-red-600 bg-clip-text text-transparent">
            {t('admin.businessManagement.title')}
          </span>
        </h2>
        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            className={`px-6 py-3 rounded-xl font-bold text-lg shadow transition-all duration-200 border-2 ${activeTab === TAB_ALL ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-500 border-red-200 hover:bg-red-100'}`}
            onClick={() => setActiveTab(TAB_ALL)}
          >
            Todos
          </button>
          <button
            className={`px-6 py-3 rounded-xl font-bold text-lg shadow transition-all duration-200 border-2 ${activeTab === TAB_CONSTRUCTION ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-500 border-red-200 hover:bg-red-100'}`}
            onClick={() => setActiveTab(TAB_CONSTRUCTION)}
          >
            Construcción
          </button>
          <button
            className={`px-6 py-3 rounded-xl font-bold text-lg shadow transition-all duration-200 border-2 ${activeTab === TAB_HEALTH ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-500 border-red-200 hover:bg-red-100'}`}
            onClick={() => setActiveTab(TAB_HEALTH)}
          >
            Salud
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600"></div>
            <span className="ml-6 text-xl text-gray-600 font-semibold animate-pulse">{t('common.loading')}</span>
          </div>
        ) : (
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/40">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-red-100 via-white to-red-50">
                    <th className="px-6 py-5 text-left text-lg font-extrabold text-gray-900 border-b-2 border-gray-200 sticky top-0 z-10">{t('admin.businessManagement.companyName')}</th>
                    <th className="px-6 py-5 text-left text-lg font-extrabold text-gray-900 border-b-2 border-gray-200 sticky top-0 z-10">{t('admin.businessManagement.businessEmail')}</th>
                    <th className="px-6 py-5 text-left text-lg font-extrabold text-gray-900 border-b-2 border-gray-200 sticky top-0 z-10">{t('admin.businessManagement.status')}</th>
                    <th className="px-6 py-5 text-left text-lg font-extrabold text-gray-900 border-b-2 border-gray-200 sticky top-0 z-10">{t('admin.businessManagement.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBusinesses.map((business, index) => (
                    <tr key={business.id} className={index % 2 === 0 ? 'bg-white/60' : 'bg-red-50/60 hover:bg-red-100/80 transition-colors duration-200'}>
                      <td className="px-6 py-5 text-gray-900 border-b border-gray-200 font-semibold text-lg">{business.companyName}</td>
                      <td className="px-6 py-5 text-gray-900 border-b border-gray-200">{business.businessEmail}</td>
                      <td className="px-6 py-5 text-gray-900 border-b border-gray-200">{t('business.statuses.'+business.status)}</td>
                      <td className="px-6 py-5 border-b border-gray-200">
                        <button 
                          onClick={() => handleView(business)}
                          className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-pink-500 hover:to-red-500 text-white border-none py-3 px-7 rounded-xl text-lg font-bold shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
                        >
                          <FaEye/>
                          {t('admin.businessManagement.view')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {modalOpen && selected && (
          <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 animate-fadeIn">
            <div className="bg-white rounded-3xl p-12 w-11/12 max-w-3xl max-h-[92vh] overflow-y-auto relative shadow-2xl border border-white/40 animate-fadeInUp">
              <button 
                onClick={handleClose}
                className="absolute top-4 right-4 bg-white/70 border border-red-200 text-red-500 hover:bg-red-500 hover:text-white cursor-pointer p-2 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 text-xl"
              >
                <FaTimes/>
              </button>
              <h2 className="text-red-600 font-extrabold text-3xl mb-8 tracking-tight drop-shadow-lg">
                {t('admin.businessManagement.detailTitle')}
              </h2>
              <div className="flex flex-col gap-3 text-lg mb-8">
                <div><span className="font-bold">{t('admin.businessManagement.companyName')}:</span> {selected.companyName}</div>
                <div><span className="font-bold">Email:</span> {selected.businessEmail}</div>
                <div><span className="font-bold">{t('business.address')}:</span> {selected.address}</div>
                <div><span className="font-bold">{t('business.phone')}:</span> {selected.phone}</div>
                <div><span className="font-bold">{t('business.employeeCount')}:</span> {selected.employeeCount}</div>
                <div><span className="font-bold">{t('business.statuses.'+selected.status)}:</span> {selected.status}</div>
                <div><span className="font-bold">Sector:</span> {selected.companySector ? selected.companySector : 'Construcción'}</div>
              </div>
              <div className="flex gap-6 justify-end">
                <button onClick={handleApprove} className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-xl shadow-xl text-lg flex items-center gap-2 transition-all duration-300">
                  <FaCheck/>
                  {t('admin.businessManagement.approve')}
                </button>
                <button onClick={handleReject} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-xl shadow-xl text-lg flex items-center gap-2 transition-all duration-300">
                  <FaTimes/>
                  {t('admin.businessManagement.reject')}
                </button>
              </div>
            </div>
          </div>
        )}
        {toast && (
          <div className={`fixed bottom-8 right-8 px-6 py-4 rounded-xl shadow-xl text-lg font-bold z-50 ${toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>{toast.message}</div>
        )}
      </div>
    </div>
  );
};

export default BusinessManagement; 